import type { InnovatorCompanyPortfolioItemDto } from '@@types/api.types';
import { Input } from '@components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDownWideNarrowIcon, ArrowUpDown, ArrowUpNarrowWideIcon, Loader2 } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

type ParentPortfolioTableProps = {
  isLoading: boolean;
  onSearchChange: (search: string | null) => void;
  onSortingChange: (sorting: SortingState) => void;
  search: string | null;
  sorting: SortingState;
  technologies: InnovatorCompanyPortfolioItemDto[];
};

export const ParentPortfolioTable: React.FC<ParentPortfolioTableProps> = (props) => {
  const { isLoading, onSearchChange, onSortingChange, search, sorting, technologies } = props;

  const navigate = useNavigate();

  const tableColumns = React.useMemo<ColumnDef<InnovatorCompanyPortfolioItemDto>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => row.name,
        header: 'Name',
        sortingFn: 'text',
        cell: ({ row }) => {
          const technology = row.original;

          return <span className="text-sm font-medium text-gray-900">{technology.name}</span>;
        },
      },
      {
        id: 'company',
        accessorFn: (row) => row.companyName,
        header: 'Organization',
        sortingFn: 'text',
        cell: ({ row }) => {
          const technology = row.original;

          return <span className="text-sm text-gray-900">{technology.companyName}</span>;
        },
      },
      {
        id: 'category',
        accessorFn: (row) => row.categoryName,
        header: 'Category',
        sortingFn: 'text',
        cell: ({ row }) => {
          const technology = row.original;

          return <span className="text-sm text-gray-900">{technology.categoryName}</span>;
        },
      },
      {
        id: 'trlScore',
        accessorFn: (row) => row.trlScore ?? -1,
        header: 'TRL',
        sortingFn: 'basic',
        cell: ({ row }) => {
          const technology = row.original;

          return (
            <span className="text-sm text-gray-900">
              {technology.trlScore !== null && technology.trlScore !== undefined ? technology.trlScore : '—'}
            </span>
          );
        },
      },
      {
        id: 'mrlScore',
        accessorFn: (row) => row.mrlScore ?? -1,
        header: 'MRL',
        sortingFn: 'basic',
        cell: ({ row }) => {
          const technology = row.original;

          return (
            <span className="text-sm text-gray-900">
              {technology.mrlScore !== null && technology.mrlScore !== undefined ? technology.mrlScore : '—'}
            </span>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    columns: tableColumns,
    data: technologies,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange,
    state: { sorting },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <Input
          className="flex-1"
          placeholder="Search by technology name"
          value={search ?? ''}
          onChange={(event) => onSearchChange(event.target.value ? event.target.value : null)}
        />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center h-48 rounded-xl border">
          <Loader2 className="size-6 animate-spin text-gray-500" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    if (header.isPlaceholder) {
                      return <TableHead key={header.id} />;
                    }

                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();

                    return (
                      <TableHead
                        key={header.id}
                        className="text-xs font-semibold text-gray-500"
                      >
                        {canSort ? (
                          <button
                            className="flex items-center gap-1 text-xs font-semibold text-gray-600"
                            onClick={header.column.getToggleSortingHandler()}
                            type="button"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                            {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                            {!sorted && <ArrowUpDown className="size-3.5 text-gray-400" />}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/technology/${row.original.id}`)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={tableColumns.length}
                    className="h-24 text-center text-sm text-gray-500"
                  >
                    No technologies found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
